import { useEffect, useState, type FormEvent } from "react";
import { CALENDAR_FEED_SCOPES, type CalendarFeedInfo, type CalendarFeedScope, type EventInfo } from "@handvero/shared";
import { api, errorText } from "../api";
import { Icon, Message, Sheet } from "./ui";

/** Einen Termin per E-Mail an jemanden außerhalb von HandVero schicken, z. B. an einen Kunden. */
export function ShareEventSheet({ event, onClose }: { event: EventInfo; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function send(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError("");
    try {
      const res = await api.post<{ ok: true; email: string }>(`/api/events/${event.id}/share`, {
        email: String(f.get("email") ?? ""),
        message: String(f.get("message") ?? "") || undefined,
        withDetails: f.get("withDetails") === "on",
      });
      setSentTo(res.email);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Termin teilen" onClose={onClose}>
      {sentTo ? (
        <div className="ev-readonly">
          <h3>Verschickt.</h3>
          <p>
            {sentTo} bekommt den Termin „{event.title}“ per E-Mail und kann ihn mit einem Klick in den eigenen Kalender übernehmen. Antworten kommen
            direkt bei Ihnen an.
          </p>
          <button className="app-btn" onClick={onClose}>
            Fertig
          </button>
        </div>
      ) : (
        <form className="app-form" onSubmit={send}>
          <p className="hint small">
            Die Person bekommt eine E-Mail mit Titel, Zeit und Ort und einer Kalenderdatei für Outlook, Google oder Apple. Ändert sich der Termin, holt
            sie sich über den Link in der Mail den aktuellen Stand.
          </p>
          <label>
            E-Mail-Adresse
            <input name="email" type="email" required autoFocus maxLength={254} placeholder="kunde@beispiel.de" autoComplete="email" />
          </label>
          <label>
            Nachricht (freiwillig)
            <textarea name="message" rows={3} maxLength={1000} placeholder="z. B. Bitte den Zählerschrank frei zugänglich lassen." />
          </label>
          {event.details && (
            <label className="check">
              <input type="checkbox" name="withDetails" />
              Notiz zum Termin mitschicken
            </label>
          )}
          <Message>{error}</Message>
          <button className="app-btn" disabled={busy}>
            {busy ? "Wird verschickt …" : "Termin verschicken"}
          </button>
        </form>
      )}
    </Sheet>
  );
}

/** Den eigenen Kalender als Abo-Link teilen: Outlook, Google oder das iPhone zeigen die Termine dann mit an. */
export function CalendarFeedPanel() {
  const [feed, setFeed] = useState<CalendarFeedInfo | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get<CalendarFeedInfo>("/api/calendar/feed").then(setFeed, () => undefined);
  }, []);
  if (!feed) return null;
  return (
    <>
      <div className={`outlook calfeed${feed.active ? " on" : ""}`}>
        <span className="outlook-logo calfeed-logo" aria-hidden="true">
          <Icon name="send" />
        </span>
        <div className="outlook-text">
          <b>{feed.active ? "Kalender geteilt" : "Kalender teilen"}</b>
          <small>
            {feed.active
              ? `${CALENDAR_FEED_SCOPES[feed.scope]}${feed.lastUsedAt ? ` · zuletzt abgerufen ${new Date(feed.lastUsedAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}` : ""}`
              : "Ihre Termine auch in Outlook, Google Kalender oder auf dem iPhone sehen, oder als Link an jemanden geben."}
          </small>
        </div>
        <div className="outlook-actions">
          <button className="app-btn small" onClick={() => setOpen(true)}>
            {feed.active ? "Link anzeigen" : "Teilen"}
          </button>
        </div>
      </div>
      {open && <FeedSheet feed={feed} onChange={setFeed} onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedSheet({ feed, onChange, onClose }: { feed: CalendarFeedInfo; onChange: (f: CalendarFeedInfo) => void; onClose: () => void }) {
  const [scope, setScope] = useState<CalendarFeedScope>(feed.scope);
  const [withDetails, setWithDetails] = useState(feed.withDetails);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  async function run(fn: () => Promise<CalendarFeedInfo>) {
    setBusy(true);
    setError("");
    try {
      onChange(await fn());
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const save = (s = scope, d = withDetails) => run(() => api.put<CalendarFeedInfo>("/api/calendar/feed", { scope: s, withDetails: d }));

  async function copy() {
    if (!feed.url) return;
    await navigator.clipboard?.writeText(feed.url).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  }

  async function stop() {
    if (!window.confirm("Kalender nicht mehr teilen? Wer den Link eingebunden hat, sieht danach keine Termine mehr.")) return;
    setBusy(true);
    try {
      await api.del("/api/calendar/feed");
      onChange({ ...feed, active: false, url: null, webcal: null, lastUsedAt: null });
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Kalender teilen" onClose={onClose}>
      <div className="settings">
        <fieldset className="choice">
          <legend>Welche Termine</legend>
          <div>
            {(Object.keys(CALENDAR_FEED_SCOPES) as CalendarFeedScope[]).map((s) => (
              <button
                key={s}
                type="button"
                className={s === scope ? "on" : undefined}
                aria-pressed={s === scope}
                disabled={busy}
                onClick={() => {
                  setScope(s);
                  if (feed.active) void save(s, withDetails);
                }}
              >
                {CALENDAR_FEED_SCOPES[s]}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="choice">
          <legend>Notizen zu Terminen</legend>
          <div>
            {([
              [false, "Nicht zeigen"],
              [true, "Mit anzeigen"],
            ] as const).map(([v, label]) => (
              <button
                key={label}
                type="button"
                className={v === withDetails ? "on" : undefined}
                aria-pressed={v === withDetails}
                disabled={busy}
                onClick={() => {
                  setWithDetails(v);
                  if (feed.active) void save(scope, v);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>

        {!feed.active && (
          <button className="app-btn" disabled={busy} onClick={() => void save()}>
            {busy ? "Wird eingerichtet …" : "Link erstellen"}
          </button>
        )}

        {feed.active && feed.url && (
          <div className="calfeed-link">
            <code>{feed.url}</code>
            <div className="calfeed-actions">
              <a className="app-btn small" href={feed.webcal ?? feed.url}>
                In Outlook oder Apple-Kalender öffnen
              </a>
              <button className="app-btn small ghost" onClick={() => void copy()}>
                {copied ? "Kopiert" : "Link kopieren"}
              </button>
            </div>
            <ul className="calfeed-help">
              <li>
                <b>Outlook:</b> Knopf oben, oder in Outlook „Kalender hinzufügen“ → „Aus dem Internet abonnieren“ und den Link einfügen.
              </li>
              <li>
                <b>Google Kalender:</b> am PC links bei „Weitere Kalender“ auf „+“ → „Per URL“ und den Link einfügen.
              </li>
              <li>
                <b>iPhone:</b> den Link auf dem iPhone öffnen und „Abonnieren“ tippen.
              </li>
            </ul>
            <p className="muted-s">
              Der Kalender wird dort nur angezeigt, nicht geändert. Neue Termine erscheinen je nach Programm nach einigen Minuten bis Stunden. Wer den
              Link hat, sieht die Termine. Geben Sie ihn nur an Personen, denen Sie vertrauen.
            </p>
            <div className="calfeed-actions">
              <button className="btn-text" disabled={busy} onClick={() => void run(() => api.post<CalendarFeedInfo>("/api/calendar/feed/renew"))}>
                Neuen Link erstellen (der alte funktioniert dann nicht mehr)
              </button>
              <button className="btn-text danger" disabled={busy} onClick={() => void stop()}>
                Nicht mehr teilen
              </button>
            </div>
          </div>
        )}
        <Message>{error}</Message>
      </div>
    </Sheet>
  );
}
